import { Card, CardContent } from "@/components/ui/card";
import { FitNumber } from "@/components/ui/big-number";
import { OverviewCardHeader } from "@/lib/dashboard/OverviewCardHeader";
import { OverviewCardRow } from "@/lib/dashboard/OverviewCardRow";
import { useI18n } from "@/hooks/useI18n";

interface OverviewStatsCardProps {
  /** Pratiche chiuse e ancora aperte: servono al tasso di conversione. */
  completedCount: number;
  activeCount: number;
  /**
   * Viaggiatori delle pratiche del perimetro, non chi ha prenotato: tre viaggi venduti a
   * tre persone da quattro partecipanti l'uno fanno dodici. `undefined` quando il DB non
   * ha ancora la migrazione che lo espone.
   */
  travelersCount: number | undefined;
  closedValueTotal: number;
  avgValuePerItinerary: number;
  marginTotal: number;
  avgMarginPercent: number;
  /** Fee di piattaforma: valorizzata solo per l'admin, `null` per tutti gli altri. */
  platformFeeTotal: number | null;
  formatCurrency: (value: number) => string;
  formatPercent: (value: number) => string;
}

/**
 * Le statistiche derivate della Panoramica.
 *
 * Stanno insieme e in fondo alla riga perche' sono numeri di secondo grado: rapporti e
 * medie, non fatti. Sparsi fra le tessere si leggevano come dati primari, e una media
 * accanto a un totale sembra un totale.
 *
 * Conversione e valore medio per viaggiatore si calcolano qui e non nella RPC: sono
 * divisioni fra numeri che la RPC gia' restituisce, e farli scendere dal server
 * significherebbe poterli far divergere da cio' che si vede.
 */
export function OverviewStatsCard({
  completedCount,
  activeCount,
  travelersCount,
  closedValueTotal,
  avgValuePerItinerary,
  marginTotal,
  avgMarginPercent,
  platformFeeTotal,
  formatCurrency,
  formatPercent,
}: OverviewStatsCardProps) {
  const { t } = useI18n();

  const totalRequests = completedCount + activeCount;
  // Senza pratiche il rapporto non e' zero, non esiste: uno "0%" si leggerebbe come "non
  // chiudiamo niente" invece che "non c'e' ancora niente da chiudere".
  const conversion =
    totalRequests > 0 ? formatPercent((completedCount / totalRequests) * 100) : "—";
  // Diviso i viaggiatori e non i clienti: chi prenota e' uno, chi parte sono quattro, e
  // sul numero di chi prenota la media raccontava un quarto del valore generato.
  const avgPerTraveler =
    travelersCount && travelersCount > 0
      ? formatCurrency(closedValueTotal / travelersCount)
      : "—";

  return (
    <Card className="h-full">
      <CardContent className="p-4 h-full flex flex-col gap-4">
        <OverviewCardHeader
          title={t("dashboard.overview.stats.title")}
          info={t("dashboard.overview.stats.info")}
        />

        {/* Il valore chiuso e' l'unico fatto della card, e sta su una riga sua: le altre
            sono tutte sue derivate, e scriverlo grande a destra dell'etichetta le tiene
            incolonnate sotto senza doverlo ripetere. */}
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-gray-600">
            {t("dashboard.overview.stats.closedValue")}
          </span>
          {/* min-w-0 + flex-1: il numero prende lo spazio rimasto accanto all'etichetta e
              rimpicciolisce dentro quello, invece di uscire dalla card. */}
          <FitNumber
            value={formatCurrency(closedValueTotal)}
            size="md"
            className="flex-1 text-right text-gray-900"
          />
        </div>

        <div className="flex flex-col gap-1.5 mt-auto pt-3 border-t border-gray-100">
          <OverviewCardRow
            label={t("dashboard.overview.stats.conversion")}
            value={conversion}
            info={t("dashboard.overview.stats.conversionInfo")}
          />
          <OverviewCardRow
            label={t("dashboard.overview.avgValuePerItinerary")}
            value={formatCurrency(avgValuePerItinerary)}
            info={t("dashboard.overview.avgValuePerItineraryInfo")}
          />
          <OverviewCardRow
            label={t("dashboard.overview.stats.avgPerTraveler")}
            value={avgPerTraveler}
            info={t("dashboard.overview.stats.avgPerTravelerInfo")}
          />
          {/* Margine e media margine sono il proprio guadagno, non un dato di piattaforma:
              li vedono tutti i ruoli che aprono questa panoramica. La fee Place2Be resta
              all'amministrazione, e la regola di visibilita' vive in un posto solo -- la
              RPC la restituisce `null` a chi non la puo' vedere. */}
          <OverviewCardRow
            label={t("dashboard.overview.marginTotal")}
            value={formatCurrency(marginTotal)}
            info={t("dashboard.overview.marginTotalInfo")}
            strong
          />
          <OverviewCardRow
            label={t("dashboard.overview.avgMarginPercent")}
            value={formatPercent(avgMarginPercent)}
            info={t("dashboard.overview.avgMarginPercentInfo")}
          />
          {platformFeeTotal != null && (
            <OverviewCardRow
              label={t("dashboard.overview.platformFeeTotal")}
              value={formatCurrency(platformFeeTotal)}
              info={t("dashboard.overview.platformFeeTotalInfo")}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default OverviewStatsCard;
